import React from "react";
import { TimelineBatch } from "../../../types/mes";
import { t } from "../../../i18n/locale";
import { groupSemiFinishedBatches } from "./planningUtils";

interface Props {
  batches: Partial<TimelineBatch>[];
  /** Production day of the finished products (parent), used as the reference. */
  parentDate: string;
  onChangeDate: (key: number, productionDate: string) => void;
  disabled?: boolean;
}

const WEEKDAYS_HR = [
  "Nedjelja",
  "Ponedjeljak",
  "Utorak",
  "Srijeda",
  "Četvrtak",
  "Petak",
  "Subota",
];

function dayDelta(productionDate: string, parentDate: string): number {
  const a = new Date(`${productionDate}T00:00:00`);
  const b = new Date(`${parentDate}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

function relativeLabel(productionDate?: string, parentDate?: string): string {
  if (!productionDate || !parentDate) return "";
  const delta = dayDelta(productionDate, parentDate);
  const weekday = t(WEEKDAYS_HR[new Date(`${productionDate}T00:00:00`).getDay()] || "");
  if (delta === 0) return t("{weekday} · isti dan", { weekday });
  if (delta === 1) return t("{weekday} · dan prije", { weekday });
  if (delta > 1) return t("{weekday} · {delta} dana prije", { weekday, delta });
  return t("{weekday} · {delta} dana kasnije", { weekday, delta: Math.abs(delta) });
}

/**
 * Wizard step 2 — staggered production of semi-finished products.
 *
 * Semi-finished products (starter, enzymes, seed mixes) are rarely made on the
 * same day as the finished goods. This step lists each one (aggregated across
 * the plan) with its planned production day prefilled from the database rule
 * (e.g. starter = day before, enzyme = Friday) and lets the user adjust it.
 */
const SemiFinishedScheduleStep: React.FC<Props> = ({
  batches,
  parentDate,
  onChangeDate,
  disabled,
}) => {
  const groups = groupSemiFinishedBatches(batches, parentDate);

  return (
    <div className="mes-section planning-section sf-schedule">
      <h3 style={{ marginTop: 0 }}>{t("2. Polugotovi proizvodi")}</h3>
      <p className="editor-hint" style={{ marginTop: 0 }}>
        {t("Starteri, enzimi i mješavine sjemena rijetko se rade isti dan kao gotovi proizvodi. Datum proizvodnje predložen je iz pravila (npr. starter dan prije, enzimi petak) — po potrebi izmijeni.")}
      </p>

      {groups.length === 0 ? (
        <div className="sf-empty">
          {t("Odabrani proizvodi nemaju polugotovih proizvoda u recepturi.")}
        </div>
      ) : (
        <div className="mes-table-wrap">
          <table className="mes-table sf-table">
          <thead>
            <tr>
              <th>{t("Polugotov proizvod")}</th>
              <th>{t("Zamjesa")}</th>
              <th>{t("Ukupna masa (kg)")}</th>
              <th>{t("Dan proizvodnje")}</th>
              <th>{t("Odnos na gotov proizvod")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key}>
                <td style={{ fontWeight: 500 }}>{g.name}</td>
                <td>{g.batchCount}</td>
                <td>{g.totalMassKg ? g.totalMassKg.toFixed(1) : "—"}</td>
                <td>
                  <input
                    type="date"
                    className="input-classic input-date"
                    value={g.productionDate ?? parentDate}
                    disabled={disabled}
                    onChange={(e) => onChangeDate(g.key, e.target.value)}
                  />
                </td>
                <td>
                  <span className="sf-rel">
                    {relativeLabel(g.productionDate, parentDate)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <style>{`
        .sf-schedule .sf-empty {
          padding: 18px;
          border: 1px dashed #4a5f7f;
          border-radius: 8px;
          color: #95a5a6;
          text-align: center;
        }
        .sf-table th, .sf-table td { white-space: nowrap; vertical-align: middle; }
        .sf-table .input-date { min-width: 150px; }
        .sf-rel {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 999px;
          background: rgba(39,174,96,0.12);
          color: #27ae60;
          font-size: 12px;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default SemiFinishedScheduleStep;
